// Kept free of React and the API client: the Worker in worker/ renders blog
// pages at the edge with these same functions, and the page renders them again
// in the browser. One function on both sides is what lets React adopt the
// edge-rendered article instead of throwing it away on hydration.

/**
 * A blog post as the storefront uses it. Posts are written and published in
 * Terradium; the Worker reads them from Terradium's content API, cleans the
 * HTML, and hands this shape to both the page and /_blog/posts/:slug.
 */
export interface BlogSummary {
  slug: string;
  title: string;
  excerpt: string;
  image?: string;
  publishedAt: string;
  readingTime?: number;
}

export interface BlogPost extends BlogSummary {
  description: string;
  /** Sanitized article HTML, starting with the post's own <h1>. */
  html: string;
  /** Terradium's schema.org graph for the post, injected verbatim. */
  jsonLd?: object;
}

/** Where the page reads posts from on in-app navigation. Served by the Worker. */
export const BLOG_DATA = "/_blog";

/** The <script type="application/json"> the Worker embeds the page's data in. */
export const BLOG_DATA_ID = "blog-data";

export function blogPath(slug: string): string {
  return `/blog/${encodeURIComponent(slug)}`;
}

/** The post as plain Markdown, for AI crawlers. Served by the Worker. */
export function blogMarkdownPath(slug: string): string {
  return `${blogPath(slug)}.md`;
}

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

/**
 * "10 Oktober 2026", in Jakarta time. Spelled out by hand rather than with
 * Intl: the Worker and the browser must produce the same string, or the
 * hydrated page differs from the one the Worker sent.
 */
export function formatDate(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "";
  const d = new Date(ms + 7 * 3600_000);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

export function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function meta(post: BlogSummary): string {
  const parts = [`<time datetime="${escapeHtml(post.publishedAt)}">${formatDate(post.publishedAt)}</time>`];
  if (post.readingTime) parts.push(`${post.readingTime} menit baca`);
  return parts.join(" · ");
}

/**
 * The article: its heading, date and reading time, the featured image, then
 * the body. The body already opens with the post's <h1>; one is added from the
 * title only when it doesn't, so the page always has exactly one.
 */
export function renderArticle(post: BlogPost): string {
  const body = /^\s*<h1[\s>]/i.test(post.html) ? post.html : `<h1>${escapeHtml(post.title)}</h1>\n${post.html}`;
  const close = body.search(/<\/h1>/i) + "</h1>".length;
  const header =
    `<p class="blog-meta">${meta(post)}</p>` +
    (post.image ? `<img class="blog-hero" src="${escapeHtml(post.image)}" alt="${escapeHtml(post.title)}">` : "");
  return `<article class="blog-article product-markdown">${body.slice(0, close)}${header}${body.slice(close)}</article>`;
}

/** The /blog list: every post, newest first, or a line saying there are none yet. */
export function renderIndex(posts: BlogSummary[]): string {
  if (posts.length === 0) return `<p class="blog-empty">Belum ada artikel. Segera hadir.</p>`;
  const cards = posts.map((p) => {
    const href = escapeHtml(blogPath(p.slug));
    const image = p.image ? `<img src="${escapeHtml(p.image)}" alt="" loading="lazy">` : "";
    return (
      `<li class="blog-card"><a href="${href}">${image}<div>` +
      `<p class="blog-meta">${meta(p)}</p>` +
      `<h2>${escapeHtml(p.title)}</h2>` +
      `<p>${escapeHtml(p.excerpt)}</p>` +
      `</div></a></li>`
    );
  });
  return `<ul class="blog-list">${cards.join("")}</ul>`;
}

/** Beranda › Blog › the post, for the post's structured data. */
export function blogBreadcrumb(post: BlogSummary, siteUrl: string): object {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Beranda", item: `${siteUrl}/` },
      { "@type": "ListItem", position: 2, name: "Blog", item: `${siteUrl}/blog` },
      { "@type": "ListItem", position: 3, name: post.title, item: siteUrl + blogPath(post.slug) },
    ],
  };
}

/** The post's structured data: Terradium's graph, then the breadcrumb. */
export function blogJsonLd(post: BlogPost, siteUrl: string): object[] {
  return [...(post.jsonLd ? [post.jsonLd] : []), blogBreadcrumb(post, siteUrl)];
}

/**
 * What a blog page is showing, as the Worker embeds it in the page and as
 * /_blog answers. The page renders its island from this and nothing else, so
 * the Worker's HTML and the browser's agree whatever state the page is in.
 */
export type PostData =
  | { kind: "post"; post: BlogPost }
  | { kind: "missing"; slug: string }
  | { kind: "error"; slug: string };

export type IndexData = { kind: "index"; posts: BlogSummary[] } | { kind: "error" };

const UNAVAILABLE = `<p class="blog-empty">Artikel belum bisa dimuat. Coba muat ulang halaman ini sebentar lagi.</p>`;

export function renderPostIsland(data: PostData): string {
  if (data.kind === "post") return renderArticle(data.post);
  if (data.kind === "missing")
    return `<h1>Artikel tidak ditemukan.</h1><p class="blog-empty">Mungkin sudah dipindah atau dihapus. <a href="/blog">Lihat semua artikel</a>.</p>`;
  return UNAVAILABLE;
}

export function renderIndexIsland(data: IndexData): string {
  return data.kind === "index" ? renderIndex(data.posts) : UNAVAILABLE;
}

export const BLOG_TITLE = "Blog — Kanata Indonesia";
export const BLOG_DESCRIPTION =
  "Panduan memilih standing desk, setup meja kerja yang ergonomis, dan tips ruang kerja dari Kanata Indonesia.";

export function postTitle(post: BlogSummary): string {
  return `${post.title} — Kanata Indonesia`;
}
